/**
 * Utilitário para padronização de estilos de botões no módulo de fossas sépticas
 * Este arquivo centraliza todos os estilos de botões para garantir consistência visual
 */

/**
 * Estilo base para todos os botões
 */
const baseButtonStyle = {
  padding: '8px 16px',
  borderRadius: '4px',
  fontSize: '14px',
  fontWeight: '500',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
  textDecoration: 'none'
};

/**
 * Estilo para botão primário (verde)
 * Usado para ações principais como "Cadastrar", "Salvar", "Atualizar"
 */
export const primaryButtonStyle = {
  ...baseButtonStyle,
  border: 'none',
  backgroundColor: '#2e7d32',
  color: 'white'
};

/**
 * Estilo para botão secundário (cinza claro)
 * Usado para ações secundárias como "Cancelar", "Voltar", "Limpar Filtros"
 */
export const secondaryButtonStyle = {
  ...baseButtonStyle,
  border: '1px solid #ced4da',
  backgroundColor: '#f8f9fa',
  color: '#333'
};

/**
 * Estilo para botão de perigo (vermelho)
 * Usado para ações destrutivas como "Excluir", "Remover"
 */
export const dangerButtonStyle = {
  ...baseButtonStyle,
  border: 'none',
  backgroundColor: '#dc3545',
  color: 'white'
};

/**
 * Estilo para botão de informação (azul)
 * Usado para ações informativas como "Ver Detalhes"
 */
export const infoButtonStyle = {
  ...baseButtonStyle,
  border: 'none',
  backgroundColor: '#0d6efd',
  color: 'white'
};

/**
 * Estilo para botão neutro (cinza)
 * Usado para ações neutras como "Ver no Mapa"
 */
export const neutralButtonStyle = {
  ...baseButtonStyle,
  border: 'none',
  backgroundColor: '#6c757d',
  color: 'white'
};

/**
 * Estilo para botão de sucesso (verde mais claro)
 * Usado para ações positivas como "Aprovar", "Visitas"
 */
export const successButtonStyle = {
  ...baseButtonStyle,
  border: 'none',
  backgroundColor: '#28a745',
  color: 'white'
};

/**
 * Função para aplicar estilo de botão desabilitado
 * @param {Object} baseStyle - Estilo base do botão
 * @param {boolean} isDisabled - Se o botão está desabilitado
 * @returns {Object} - Estilo com modificações para estado desabilitado
 */
export const applyDisabledStyle = (baseStyle, isDisabled) => {
  if (!isDisabled) return baseStyle;
  
  return {
    ...baseStyle,
    cursor: 'not-allowed',
    opacity: 0.7
  };
};

/**
 * Função para aplicar estilo de botão pequeno
 * @param {Object} baseStyle - Estilo base do botão
 * @returns {Object} - Estilo com modificações para tamanho pequeno
 */
export const applySmallButtonStyle = (baseStyle) => {
  return {
    ...baseStyle,
    padding: '4px 8px',
    fontSize: '12px'
  };
};

/**
 * Função para aplicar estilo de botão grande
 * @param {Object} baseStyle - Estilo base do botão
 * @returns {Object} - Estilo com modificações para tamanho grande
 */
export const applyLargeButtonStyle = (baseStyle) => {
  return {
    ...baseStyle,
    padding: '10px 20px',
    fontSize: '16px'
  };
};

/**
 * Função para aplicar estilo de botão apenas com ícone
 * @param {Object} baseStyle - Estilo base do botão
 * @returns {Object} - Estilo com modificações para botão apenas com ícone
 */
export const applyIconOnlyStyle = (baseStyle) => {
  return {
    ...baseStyle,
    padding: '8px',
    justifyContent: 'center'
  };
};

/**
 * Função para obter estilo de botão com base no tipo
 * @param {string} type - Tipo de botão ('primary', 'secondary', 'danger', 'info', 'neutral', 'success')
 * @param {Object} options - Opções adicionais (disabled, small, large, iconOnly)
 * @returns {Object} - Estilo completo do botão
 */
export const getButtonStyle = (type, options = {}) => {
  const { disabled = false, small = false, large = false, iconOnly = false } = options;
  
  let baseStyle;
  
  switch (type) {
    case 'primary':
      baseStyle = primaryButtonStyle;
      break;
    case 'secondary':
      baseStyle = secondaryButtonStyle;
      break;
    case 'danger':
      baseStyle = dangerButtonStyle;
      break;
    case 'info':
      baseStyle = infoButtonStyle;
      break;
    case 'neutral':
      baseStyle = neutralButtonStyle;
      break;
    case 'success':
      baseStyle = successButtonStyle;
      break;
    default:
      baseStyle = primaryButtonStyle;
  }
  
  let style = { ...baseStyle };
  
  if (small) style = applySmallButtonStyle(style);
  if (large) style = applyLargeButtonStyle(style);
  if (iconOnly) style = applyIconOnlyStyle(style);
  if (disabled) style = applyDisabledStyle(style, true);
  
  return style;
};

export default {
  primaryButtonStyle,
  secondaryButtonStyle,
  dangerButtonStyle,
  infoButtonStyle,
  neutralButtonStyle,
  successButtonStyle,
  getButtonStyle,
  applyDisabledStyle,
  applySmallButtonStyle,
  applyLargeButtonStyle,
  applyIconOnlyStyle
};